import { getAuthToken } from '@common';
import { Expire } from '@common';
import { User } from '@common';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const MPAPISlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: '/api',
        prepareHeaders: (headers, { getState }) => {
            const authToken = getAuthToken();
            if (authToken) {
                headers.set('Authorization', `Bearer ${authToken}`);
            }
            return headers;
        },
    }),
    tagTypes: ['AppProjectList', 'AppAccess', 'ApiKeys', 'OrganizationList', 'ProfilePicture', 'UserTokens'],
    endpoints: builder => ({
        checkAuth: builder.mutation<Omit<User, 'password'>, undefined>({
            query: () => 'auth/checkAuth',
        }),

        isUriValidWithIFrame: builder.query<boolean, string>({
            query: name => `app-project/is-valid-app-uri/${name}`,
            providesTags: (_result, _error, arg) => [{ type: 'AppAccess', id: arg }],
        }),

        getTempUrl: builder.query<{ url: string }, { appName?: string; expiresIn?: Expire }>({
            query: ({ appName, expiresIn }) => ({
                url: `app-project/temp-url/${appName}`,
                params: { expiresIn },
            }),
        }),

        getUserId: builder.mutation<Pick<User, 'email' | 'id'>, string>({
            query: (email: string) => ({
                url: 'users/user-id',
                params: { email },
            }),
        }),

        getNewObjectId: builder.mutation<{ newObjectId: string }, undefined>({
            query: () => 'new-object-id',
        }),

        searchUsers: builder.query<User[], string>({
            query: (query: string) => ({
                url: 'users/search',
                params: { query },
            }),
        }),

        sendAppErrorMessage: builder.mutation<void, { message: string; appName: string }>({
            query: ({ message, appName }) => ({
                url: `app-project/send-app-error-mail-to-owner/${appName}`,
                method: 'POST',
                body: { message },
            }),
        }),
    }),
});

export const {
    useCheckAuthMutation,
    useIsUriValidWithIFrameQuery,
    useGetTempUrlQuery,
    useGetUserIdMutation,
    useGetNewObjectIdMutation,
    useSearchUsersQuery,
    useSendAppErrorMessageMutation,
} = MPAPISlice;
